import { MapView } from '../components/map/MapView';
import { TrafficSignalDot } from '../components/map/TrafficSignalDot';
import { IntersectionMarkers } from '../components/map/IntersectionMarkers';
import { CabinetMarker } from '../components/map/CabinetMarker';
import { FloatingPanel } from '../components/map/FloatingPanel';
import { PhaseTimeline } from '../components/map/PhaseTimeline';
import { useIntersectionState } from '../hooks/useIntersectionState';
import {
  guadalajaraStaticSignals,
  guadalajaraIntersections,
  GUADALAJARA_BOUNDS,
  GUADALAJARA_CENTER,
  GUADALAJARA_ZOOM,
} from '../data/guadalajara';
import { RegulatorState } from '../types/protocol';
import type { IntersectionDefinition } from '../types/intersection';

const PHASES = [
  { id: 'A', duration: 300 },
  { id: 'B', duration: 50 },
  { id: 'C', duration: 300 },
  { id: 'D', duration: 50 },
];

/** Renders one simulated regulator: animated markers + cabinet */
function SimulatedRegulator({ intersection }: { intersection: IntersectionDefinition }) {
  const {
    groupColors,
    currentPhase,
    cyclePosition,
    cycleTime,
    isRunning,
  } = useIntersectionState(intersection);

  return (
    <>
      <IntersectionMarkers
        groups={intersection.groups}
        groupColors={groupColors}
      />
      <CabinetMarker
        cabinet={intersection.cabinet}
        state={RegulatorState.REMOTE_CONTROL}
        isConnected={isRunning}
        currentPlan={1}
        currentPhase={currentPhase}
        cyclePosition={cyclePosition}
        cycleTime={cycleTime}
        groupCount={intersection.groups.length}
        detectorCount={0}
      />
    </>
  );
}

export function GuadalajaraMapView() {
  // Shared cycle state for the timeline
  const { currentPhase, cyclePosition, cycleTime } =
    useIntersectionState(guadalajaraIntersections[0]);

  const totalGroups = guadalajaraIntersections.reduce((sum, i) => sum + i.groups.length, 0);

  return (
    <div className="relative w-full h-full">
      <div className="absolute inset-0 map-container">
        <MapView
          center={GUADALAJARA_CENTER}
          zoom={GUADALAJARA_ZOOM}
          cameraControl
          bearing={0}
          pitch={0}
          maxBounds={GUADALAJARA_BOUNDS}
          minZoom={13}
          maxZoom={19}
        >
          {guadalajaraStaticSignals.map(signal => (
            <TrafficSignalDot key={signal.id} signal={signal} />
          ))}

          {guadalajaraIntersections.map(intersection => (
            <SimulatedRegulator key={intersection.id} intersection={intersection} />
          ))}
        </MapView>
      </div>

      {/* Cycle timeline */}
      <FloatingPanel
        title="Ciclo"
        position="bottom-center"
      >
        <PhaseTimeline
          phases={PHASES}
          currentPhase={currentPhase}
          cyclePosition={cyclePosition}
          cycleTime={cycleTime}
        />
      </FloatingPanel>

      {/* Info badge */}
      <div className="absolute top-3 right-3 bg-gray-900/90 backdrop-blur-sm rounded-lg border border-gray-700 px-3 py-2 text-xs text-gray-300 z-40">
        <span className="font-medium text-white">Guadalajara</span>
        <span className="mx-1.5 text-gray-600">&middot;</span>
        34 semáforos
        <span className="mx-1.5 text-gray-600">&middot;</span>
        <span className="text-green-400">{guadalajaraIntersections.length} reguladores · {totalGroups} grupos</span>
      </div>
    </div>
  );
}
